import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, TrendingUp, Users, Globe, Award, Target, Heart } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { COMPANY_INFO } from '../data/companyData';

const AboutPage = () => {
  const { language } = useLanguage();

  const values = [
    {
      icon: Shield,
      title: { es: 'Profesionalismo', en: 'Professionalism' },
      desc: { es: 'Excelencia en cada negociación y proceso', en: 'Excellence in every negotiation and process' }
    },
    {
      icon: Lock,
      title: { es: 'Confidencialidad', en: 'Confidentiality' },
      desc: { es: 'Tu información siempre protegida', en: 'Your information always protected' }
    },
    {
      icon: TrendingUp,
      title: { es: 'Crecimiento', en: 'Growth' },
      desc: { es: 'Estrategias a largo plazo', en: 'Long-term strategies' }
    },
    {
      icon: Heart,
      title: { es: 'Pasión', en: 'Passion' },
      desc: { es: 'Meraki: poner el alma en todo', en: 'Meraki: putting soul into everything' }
    }
  ];

  const team = [
    {
      name: 'Alejandra Fernández',
      role: { es: 'CEO & Fundadora', en: 'CEO & Founder' },
      image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400',
      bio: { 
        es: '15 años de experiencia en la industria del entretenimiento. Ex-directora de talento en Televisa.',
        en: '15 years of experience in the entertainment industry. Former talent director at Televisa.'
      }
    },
    {
      name: 'Ricardo Sánchez',
      role: { es: 'Director de Deportes', en: 'Sports Director' },
      image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400',
      bio: {
        es: 'Ex-agente FIFA con más de 200 transferencias gestionadas.',
        en: 'Former FIFA agent with over 200 managed transfers.'
      }
    },
    {
      name: 'Sofía Martínez',
      role: { es: 'Directora Digital', en: 'Digital Director' },
      image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=400',
      bio: {
        es: 'Pionera en marketing de influencers en Latinoamérica.',
        en: 'Pioneer in influencer marketing in Latin America.'
      }
    },
    {
      name: 'Carlos Ramírez',
      role: { es: 'Director Financiero', en: 'CFO' },
      image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400',
      bio: {
        es: 'MBA por IPADE. 20 años en gestión financiera de entretenimiento.',
        en: 'MBA from IPADE. 20 years in entertainment financial management.'
      }
    }
  ];

  const milestones = [
    { year: '2021', title: { es: 'Fundación', en: 'Foundation' }, desc: { es: 'MERAKI Entertainment inicia operaciones en CDMX', en: 'MERAKI Entertainment starts operations in Mexico City' } },
    { year: '2022', title: { es: 'Expansión', en: 'Expansion' }, desc: { es: 'Alcanzamos 20 talentos y $50M en contratos', en: 'We reached 20 talents and $50M in contracts' } },
    { year: '2023', title: { es: 'Internacional', en: 'International' }, desc: { es: 'Abrimos operaciones en USA y España', en: 'We opened operations in USA and Spain' } },
    { year: '2024', title: { es: 'Liderazgo', en: 'Leadership' }, desc: { es: 'Top 3 agencias de talento en México', en: 'Top 3 talent agencies in Mexico' } }
  ];

  return (
    <>
      {/* Hero */}
      <section className="pt-32 pb-20 bg-[#050505] relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/7433853/pexels-photo-7433853.jpeg?auto=compress&w=1920"
            alt="MERAKI Office"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#050505]/80 to-[#050505]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#D91616] mb-4 block">
              {language === 'es' ? 'Quiénes Somos' : 'About Us'}
            </span>
            <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight uppercase text-white mb-6">
              {language === 'es' ? 'LA PASIÓN DE HACER LAS COSAS CON EL ALMA' : 'THE PASSION OF DOING THINGS WITH SOUL'}
            </h1>
            <p className="text-xl text-zinc-400 leading-relaxed">
              {language === 'es'
                ? 'MERAKI (μεράκι) es una palabra griega que describe el amor, la creatividad y el alma que ponemos en lo que hacemos. Esa es nuestra filosofía.'
                : 'MERAKI (μεράκι) is a Greek word that describes the love, creativity, and soul we put into what we do. That is our philosophy.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story */}
      <section className="py-24 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-white mb-6">
                {language === 'es' ? 'Nuestra Historia' : 'Our Story'}
              </h2>
              <div className="space-y-4 text-zinc-400">
                <p>
                  {language === 'es'
                    ? `Fundada en ${COMPANY_INFO.founded} en la Ciudad de México, MERAKI Entertainment nació de la visión de crear una agencia de representación diferente: una que realmente pusiera a sus talentos en primer lugar.`
                    : `Founded in ${COMPANY_INFO.founded} in Mexico City, MERAKI Entertainment was born from the vision of creating a different representation agency: one that truly put its talents first.`}
                </p>
                <p>
                  {language === 'es'
                    ? 'Nuestra fundadora, Alejandra Fernández, identificó una brecha en el mercado: los talentos mexicanos necesitaban representación de clase mundial que entendiera tanto el mercado local como las oportunidades globales.'
                    : 'Our founder, Alejandra Fernández, identified a gap in the market: Mexican talents needed world-class representation that understood both the local market and global opportunities.'}
                </p>
                <p>
                  {language === 'es'
                    ? 'Hoy, con más de 47 talentos en nuestro roster y presencia en 12 países, somos la agencia de representación de mayor crecimiento en Latinoamérica.'
                    : 'Today, with over 47 talents in our roster and presence in 12 countries, we are the fastest-growing representation agency in Latin America.'}
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-4"
            >
              <div className="space-y-4">
                <img src="https://images.pexels.com/photos/8353820/pexels-photo-8353820.jpeg?auto=compress&w=400" alt="Meeting" className="w-full aspect-square object-cover" />
                <img src="https://images.pexels.com/photos/15057805/pexels-photo-15057805.jpeg?auto=compress&w=400" alt="Mexico City" className="w-full aspect-[4/3] object-cover" />
              </div>
              <div className="space-y-4 pt-8">
                <img src="https://images.unsplash.com/photo-1758518726609-c551f858cd5c?w=400" alt="Team" className="w-full aspect-[4/3] object-cover" />
                <img src="https://images.unsplash.com/photo-1758518727707-b023e285b709?w=400" alt="Office" className="w-full aspect-square object-cover" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-outfit text-3xl sm:text-4xl font-bold text-white mb-16 text-center"
          >
            {language === 'es' ? 'Nuestra Trayectoria' : 'Our Journey'}
          </motion.h2>

          <div className="relative">
            <div className="absolute left-1/2 top-0 bottom-0 w-px bg-[#D91616]/30 hidden md:block" />
            
            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <motion.div
                  key={milestone.year}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`flex items-center gap-8 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                >
                  <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                    <div className="bg-[#0A0A0A] border border-white/10 p-6 inline-block">
                      <span className="text-[#D91616] font-bold text-2xl">{milestone.year}</span>
                      <h3 className="text-white font-semibold mt-2">{milestone.title[language]}</h3>
                      <p className="text-zinc-500 text-sm mt-1">{milestone.desc[language]}</p>
                    </div>
                  </div>
                  <div className="hidden md:block w-4 h-4 bg-[#D91616] rounded-full relative z-10" />
                  <div className="flex-1 hidden md:block" />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#D91616] mb-4 block">
              {language === 'es' ? 'Nuestros Valores' : 'Our Values'}
            </span>
            <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-white">
              {language === 'es' ? 'LO QUE NOS DEFINE' : 'WHAT DEFINES US'}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-[#050505] border border-white/10 p-8 text-center group hover:-translate-y-2 transition-all"
              >
                <div className="w-16 h-16 mx-auto mb-6 bg-[#D91616]/10 flex items-center justify-center group-hover:bg-[#D91616]/20 transition-colors">
                  <value.icon className="w-8 h-8 text-[#D91616]" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{value.title[language]}</h3>
                <p className="text-sm text-zinc-500">{value.desc[language]}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-24 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#D91616] mb-4 block">
              {language === 'es' ? 'Liderazgo' : 'Leadership'}
            </span>
            <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-white">
              {language === 'es' ? 'NUESTRO EQUIPO DIRECTIVO' : 'OUR LEADERSHIP TEAM'}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {team.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group"
              >
                <div className="relative overflow-hidden mb-4">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full aspect-[3/4] object-cover grayscale group-hover:grayscale-0 transition-all duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050505] to-transparent opacity-60" />
                </div>
                <h3 className="text-lg font-bold text-white">{member.name}</h3>
                <p className="text-[#D91616] text-sm font-medium mb-2">{member.role[language]}</p>
                <p className="text-zinc-500 text-sm">{member.bio[language]}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-[#D91616]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl md:text-5xl font-black text-white">{COMPANY_INFO.stats.talents}+</div>
              <div className="text-white/70 text-sm uppercase tracking-wider mt-2">{language === 'es' ? 'Talentos' : 'Talents'}</div>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-black text-white">{COMPANY_INFO.stats.reach}</div>
              <div className="text-white/70 text-sm uppercase tracking-wider mt-2">{language === 'es' ? 'Alcance Total' : 'Total Reach'}</div>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-black text-white">{COMPANY_INFO.stats.brands}+</div>
              <div className="text-white/70 text-sm uppercase tracking-wider mt-2">{language === 'es' ? 'Marcas Aliadas' : 'Partner Brands'}</div>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-black text-white">{COMPANY_INFO.stats.countries}+</div>
              <div className="text-white/70 text-sm uppercase tracking-wider mt-2">{language === 'es' ? 'Países' : 'Countries'}</div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default AboutPage;
